/**
 * Research Dashboard - Main page component
 * Provides UI for selecting dimensions and viewing research data
 */

import React, { useState } from 'react';
import type { DimensionType, EntityType, LimitOption } from '../../types/research-service';
import {
  useTopics,
  useOpinions,
  useQuestions,
  useCategories,
  useProducts,
  useThreads,
  useTopicGaps,
  useQuestionGaps,
} from '../../hooks/useResearchData';
import { ResearchDataViewer } from './ResearchDataViewer';
import { GapAnalysisViewer } from './GapAnalysisViewer';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useSite } from '../../contexts/SiteContext';

export function ResearchDashboard() {
  const { selectedSite } = useSite();

  // Form input state (what user types)
  const [dimension, setDimension] = useState<DimensionType>('sector');
  const [inputValue, setInputValue] = useState('');
  const [limit, setLimit] = useState<LimitOption>(10);
  const [activeTab, setActiveTab] = useState<EntityType>('topics');

  // Search state (what's actually being queried)
  const [searchDimension, setSearchDimension] = useState<DimensionType>('sector');
  const [searchValue, setSearchValue] = useState('');
  const [searchLimit, setSearchLimit] = useState<LimitOption>(10);

  // Only enable queries when we have a search value (not just input value)
  const enabled = searchValue.trim().length > 0;

  // Handle search button click or Enter key
  const handleSearch = () => {
    if (inputValue.trim().length > 0) {
      setSearchDimension(dimension);
      setSearchValue(inputValue);
      setSearchLimit(limit);
    }
  };

  // Handle Enter key in input
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Fetch data for all entity types (using search state, not input state)
  const topics = useTopics({ dimension: searchDimension, value: searchValue, limit: searchLimit }, enabled);
  const opinions = useOpinions({ dimension: searchDimension, value: searchValue, limit: searchLimit }, enabled);
  const questions = useQuestions({ dimension: searchDimension, value: searchValue, limit: searchLimit }, enabled);
  const categories = useCategories({ dimension: searchDimension, value: searchValue, limit: searchLimit }, enabled);
  const products = useProducts({ dimension: searchDimension, value: searchValue, limit: searchLimit }, enabled);
  const threads = useThreads({ dimension: searchDimension, value: searchValue, limit: searchLimit }, enabled);

  // Gap analysis (only if site is selected)
  const customerId = selectedSite?.id || selectedSite?._id || '';
  const gapsEnabled = enabled && customerId.length > 0;
  const topicGaps = useTopicGaps(
    { dimension: searchDimension, value: searchValue, customerId },
    gapsEnabled
  );
  const questionGaps = useQuestionGaps(
    { dimension: searchDimension, value: searchValue, customerId },
    gapsEnabled
  );

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Research Intelligence</h1>
        <p className="text-gray-600 mt-1">
          Explore topics, opinions, questions, and content gaps from Reddit research
        </p>
      </div>

      {/* Query Builder */}
      <Card>
        <CardHeader>
          <CardTitle>Search Parameters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Dimension Selector */}
            <div>
              <Label htmlFor="dimension">Dimension</Label>
              <Select
                value={dimension}
                onValueChange={(val) => setDimension(val as DimensionType)}
              >
                <SelectTrigger id="dimension">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sector">Sector</SelectItem>
                  <SelectItem value="category">Category</SelectItem>
                  <SelectItem value="product">Product</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Value Input */}
            <div className="md:col-span-2">
              <Label htmlFor="value">
                {dimension.charAt(0).toUpperCase() + dimension.slice(1)} Value
              </Label>
              <Input
                id="value"
                placeholder={`e.g., ${
                  dimension === 'sector'
                    ? 'beauty, healthcare'
                    : dimension === 'category'
                    ? 'skincare, fitness'
                    : 'moisturizer, protein powder'
                }`}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
              />
            </div>

            {/* Limit Selector */}
            <div>
              <Label htmlFor="limit">Results Limit</Label>
              <Select
                value={limit.toString()}
                onValueChange={(val) => setLimit(parseInt(val) as LimitOption)}
              >
                <SelectTrigger id="limit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 results</SelectItem>
                  <SelectItem value="20">20 results</SelectItem>
                  <SelectItem value="50">50 results</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Search Button */}
          <div className="mt-4 flex items-center gap-3">
            <Button
              onClick={handleSearch}
              disabled={inputValue.trim().length === 0}
            >
              Search
            </Button>
            {!selectedSite && (
              <p className="text-sm text-amber-600">
                Select a site to enable gap analysis
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results Tabs */}
      {enabled && (
        <Tabs value={activeTab} onValueChange={(val) => setActiveTab(val as EntityType)}>
          <TabsList className="grid grid-cols-7 w-full">
            <TabsTrigger value="topics">Topics</TabsTrigger>
            <TabsTrigger value="opinions">Opinions</TabsTrigger>
            <TabsTrigger value="questions">Questions</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="products">Products</TabsTrigger>
            <TabsTrigger value="threads">Threads</TabsTrigger>
            <TabsTrigger value="gaps" disabled={!gapsEnabled}>
              Gaps {!gapsEnabled && '🔒'}
            </TabsTrigger>
          </TabsList>

          {/* Topics Tab */}
          <TabsContent value="topics">
            <ResearchDataViewer
              data={topics.data?.data || []}
              title={`Topics: ${searchDimension} = "${searchValue}"`}
              loading={topics.loading}
              error={topics.error}
              onRefresh={topics.refetch}
            />
          </TabsContent>

          {/* Opinions Tab */}
          <TabsContent value="opinions">
            <ResearchDataViewer
              data={opinions.data?.data || []}
              title={`Opinions: ${searchDimension} = "${searchValue}"`}
              loading={opinions.loading}
              error={opinions.error}
              onRefresh={opinions.refetch}
            />
          </TabsContent>

          {/* Questions Tab */}
          <TabsContent value="questions">
            <ResearchDataViewer
              data={questions.data?.data || []}
              title={`Questions: ${searchDimension} = "${searchValue}"`}
              loading={questions.loading}
              error={questions.error}
              onRefresh={questions.refetch}
            />
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <ResearchDataViewer
              data={categories.data?.data || []}
              title={`Categories: ${searchDimension} = "${searchValue}"`}
              loading={categories.loading}
              error={categories.error}
              onRefresh={categories.refetch}
            />
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <ResearchDataViewer
              data={products.data?.data || []}
              title={`Products: ${searchDimension} = "${searchValue}"`}
              loading={products.loading}
              error={products.error}
              onRefresh={products.refetch}
            />
          </TabsContent>

          {/* Threads Tab */}
          <TabsContent value="threads">
            <ResearchDataViewer
              data={threads.data?.data || []}
              title={`Threads: ${searchDimension} = "${searchValue}"`}
              loading={threads.loading}
              error={threads.error}
              onRefresh={threads.refetch}
            />
          </TabsContent>

          {/* Gap Analysis Tab */}
          <TabsContent value="gaps">
            <div className="space-y-4">
              <GapAnalysisViewer
                gaps={topicGaps.data?.gaps || []}
                title={`Topic Gaps for ${selectedSite?.domain || 'selected site'}`}
                loading={topicGaps.loading}
                error={topicGaps.error}
                onRefresh={topicGaps.refetch}
              />
              <GapAnalysisViewer
                gaps={questionGaps.data?.gaps || []}
                title={`Question Gaps for ${selectedSite?.domain || 'selected site'}`}
                loading={questionGaps.loading}
                error={questionGaps.error}
                onRefresh={questionGaps.refetch}
              />
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Empty State */}
      {!enabled && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">
              Enter a {dimension} value and click Search to explore research data
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ResearchDashboard;
